import React, { useContext, useEffect, useState } from "react";
import VRamFilter from "./VramFilter";
import {
  GetProviderName,
  GPUInstanceType,
} from "components/Environment/Settings/Tabs/Compute/InstanceChanger/GPUTypes";
import { OrgContext } from "contexts/OrgContext";
import { memoryToGiB } from "components/utils";
import CloudFilter from "./CloudFilter";
import MoreFiltersPopover from "./MoreFiltersPopover";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import { UserContext } from "contexts/UserContext";
import { isNvidiaEmail } from "utils";
import CapabilityFilter from "./CapabilityFilter";

interface FilterBarProps {
  allGPUInstances: GPUInstanceType[];
  displayGPUInstances: GPUInstanceType[];
  setDisplayGPUInstances: (instances: GPUInstanceType[]) => void;
  containerSelected: ContainerSelected;
  setSelectedCloud: (cloudName: string) => void;
  selectedCloud: string;
}

const FilterBar: React.FC<FilterBarProps> = ({
  allGPUInstances,
  displayGPUInstances,
  setDisplayGPUInstances,
  containerSelected,
  selectedCloud,
  setSelectedCloud,
}) => {
  const userContext = useContext(UserContext);
  // Attributes
  const [selectedVRam, setSelectedVRam] = useState<string>("any");
  const [selectedGPUCount, setSelectedGPUCount] = useState<string>("any");
  const [selectMinRam, setSelectMinRam] = useState<string>("any");
  const [selectedMinCPUCount, setSelectedMinCPUCount] = useState<number>(0);
  const [priceRange, setPriceRange] = useState<number[]>([0, 100]);
  const [filterCount, setFilterCount] = useState<number>(0);
  const [selectedProvider, setSelectedProvider] = useState<string>("any");
  const [persistentOnly, setPersistentOnly] = useState<boolean>(false);
  const [flexiblePortControlOnly, setFlexiblePortControlOnly] =
    useState<boolean>(false);

  useEffect(() => {
    let filterCount = 0;
    if (!allGPUInstances) return;
    let filteredInstances = allGPUInstances;
    if (selectedVRam !== "any") {
      filteredInstances = filteredInstances.filter(
        (instance) =>
          memoryToGiB(instance.supported_gpus[0].memory) >=
          memoryToGiB(selectedVRam)
      );
    }
    if (selectedGPUCount != "any") {
      filteredInstances = filteredInstances.filter((instance) => {
        if (selectedGPUCount !== "8+") {
          return instance.supported_gpus[0].count === Number(selectedGPUCount);
        } else {
          return instance.supported_gpus[0].count >= 8;
        }
      });
      filterCount++;
    }
    if (selectMinRam !== "any") {
      filteredInstances = filteredInstances.filter(
        (instance) =>
          memoryToGiB(String(instance.memory)) >= memoryToGiB(selectMinRam)
      );
      filterCount++;
    }
    if (selectedMinCPUCount > 0) {
      filteredInstances = filteredInstances.filter(
        (instance) => instance.vcpu >= selectedMinCPUCount
      );
      filterCount++;
    }
    if (priceRange[0] !== 0 || priceRange[1] !== 100) {
      filteredInstances = filteredInstances.filter(
        (instance) =>
          Number(instance.base_price?.amount || 100) <= priceRange[1] &&
          Number(instance.base_price?.amount || 0) >= priceRange[0]
      );
      filterCount++;
    }
    if (selectedCloud === "Brev Cloud") {
      filteredInstances = filteredInstances.filter((instance) =>
        instance.workspace_groups.some((group) => group.tenantType === "shared")
      );
      if (selectedProvider !== "any") {
        filteredInstances = filteredInstances.filter(
          (instance) => GetProviderName(instance) === selectedProvider
        );
      }
    } else {
      filteredInstances = filteredInstances.filter((instance) =>
        instance.workspace_groups.some((group) => group.id === selectedCloud)
      );
    }

    // Filter by instance capabilities
    if (persistentOnly) {
      filteredInstances = filteredInstances.filter(
        (instance) => instance.stoppable === true
      );
    }

    // Filter by port control capabilities
    if (flexiblePortControlOnly) {
      filteredInstances = filteredInstances.filter(
        (instance) => instance.can_modify_firewall_rules === true
      );
    }

    //Filter out akash
    filteredInstances = filteredInstances.filter(
      (instance) => instance.workspace_groups[0].platformType !== "akash"
    );

    setFilterCount(filterCount);
    setDisplayGPUInstances(filteredInstances);
  }, [
    selectedVRam,
    selectedGPUCount,
    selectMinRam,
    selectedMinCPUCount,
    priceRange,
    selectedCloud,
    selectedProvider,
    persistentOnly,
    flexiblePortControlOnly,
    allGPUInstances,
    userContext.me,
  ]);

  return (
    <div className="overflow-auto flex flex-row justify-between w-full">
      <CapabilityFilter
        persistentOnly={persistentOnly}
        setPersistentOnly={setPersistentOnly}
        flexiblePortControlOnly={flexiblePortControlOnly}
        setFlexiblePortControlOnly={setFlexiblePortControlOnly}
      />
      <div className="flex flex-row justify-center">
        <VRamFilter
          selectedVRam={selectedVRam}
          setSelectedVRam={setSelectedVRam}
        />
        <CloudFilter
          selectedCloud={selectedCloud}
          setSelectedCloud={setSelectedCloud}
          selectedProvider={selectedProvider}
          setSelectedProvider={setSelectedProvider}
          containerSelected={containerSelected}
        />
        <MoreFiltersPopover
          selectedGPUCount={selectedGPUCount}
          setSelectedGPUCount={setSelectedGPUCount}
          selectedMinCPUCount={selectedMinCPUCount}
          setSelectedMinCPUCount={setSelectedMinCPUCount}
          selectMinRam={selectMinRam}
          setSelectMinRam={setSelectMinRam}
          priceRange={priceRange}
          setPriceRange={setPriceRange}
        />
      </div>
    </div>
  );
};

export default FilterBar;
